import React, { useEffect, useCallback } from "react";
import { FormControl } from "../../Form/FormControls";
import { setDataSourceColumnMetadata } from "../../../api/dataSourceMutations";
import DataTable from "../../Table/DataTable";
import { StyledSelect } from "../../Form/FormControls";
import { useApi } from "../../../api/useApi";
import { dataSourceColumns } from "../../../api/dataSourceQueries";
import { isAcesIntegrationAvailable } from "../../../api/configurationQueries";
import ErrorMessages from "../../Notifications/ErrorMessages";
import columnFlagUpdateOptions from "../../../common/helpers/datasources/columnFlagUpdateOptions";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const typeOptions = [
  { label: "DATE TIME", value: "DATE_TIME" },
  { label: "DECIMAL", value: "DECIMAL" },
  { label: "EMAIL", value: "EMAIL" },
  { label: "PHONE NUMBER", value: "PHONE_NUMBER" },
  { label: "STRING", value: "STRING" },
  { label: "WHOLE NUMBER", value: "WHOLE_NUMBER" },
  { label: "BOOLEAN", value: "BOOLEAN" },
  { label: "UUID", value: "UUID" },
  { label: "DATE TIME OFFSET", value: "DATE_TIME_OFFSET" },
  { label: "TIMESPAN", value: "TIMESPAN" },
];

// Bits of the `flags` bitmask each checkbox column represents.
const COLUMN_FLAGS = {
  NULLABLE: 1,
  UNIQUE: 2,
  PRIMARY_KEY: 4,
  SENSITIVE: 128,
  ACES_REFERENCE: 256,
};

// The checkbox columns are all bits of the same `flags` value, so they used to
// share `accessor: (d) => d.flags` — every one of them sorted and filtered on the
// identical raw bitmask, which is why they were opted out of the column menu.
// Exposing each column's own Yes/No lets the menu filter and sort on the state
// the checkbox actually shows (AC-15898). Display still comes from the Cell.
const flagValue = (flags, bit) => (((flags ?? 0) & bit) > 0 ? "Yes" : "No");

const ColumnView = ({ sourceId }) => {
  const [{ loading, errors, data: apiData }, getSource] = useApi(
    dataSourceColumns,
    {
      id: Number(sourceId),
    }
  );

  const [
    { loading: updating, errors: updateErrors, data: updateData },
    updateFetch,
  ] = useApi();

  // Legacy (self-hosted) deployments have no link to ACES, so the ACES Reference flag is only offered when the
  // server says ACES integration is available. Treated as unavailable until that answer arrives.
  const [{ data: acesIntegrationData }] = useApi(isAcesIntegrationAvailable);
  const acesIntegrationEnabled = acesIntegrationData?.isAcesIntegrationAvailable === true;

  const columns = apiData?.dataSource?.columns ?? [];

  const getUpdatedSource = useCallback(() => {
    getSource({
      query: dataSourceColumns,
      variables: { id: Number(sourceId) },
    });
  }, [sourceId, getSource]);

  //callback
  const updateNull = useCallback(
    (columnData, nullable) => {
      const variables = {
        metadata: {
          columnId: columnData.id,
          description: null,
          dataType: null,
          flagAction: nullable
            ? columnFlagUpdateOptions.addNullability
            : columnFlagUpdateOptions.removeNullability,
        },
      };

      updateFetch({ query: setDataSourceColumnMetadata, variables });
    },
    [updateFetch]
  );

  const updatePrimaryKey = useCallback(
    (columnData, isPrimaryKey) => {
      const variables = {
        metadata: {
          columnId: columnData.id,
          description: null,
          dataType: null,
          flagAction: isPrimaryKey
            ? columnFlagUpdateOptions.addPrimaryKey
            : columnFlagUpdateOptions.removePrimaryKey,
        },
      };

      updateFetch({ query: setDataSourceColumnMetadata, variables });
    },
    [updateFetch]
  );

  const updateUnique = useCallback(
    (columnData, isUnique) => {
      const variables = {
        metadata: {
          columnId: columnData.id,
          description: null,
          dataType: null,
          flagAction: isUnique
            ? columnFlagUpdateOptions.addUnique
            : columnFlagUpdateOptions.removeUnique,
        },
      };

      updateFetch({ query: setDataSourceColumnMetadata, variables });
    },
    [updateFetch]
  );

  const updateSensitive = useCallback(
    (columnData, isPii) => {
      const variables = {
        metadata: {
          columnId: columnData.id,
          description: null,
          dataType: null,
          flagAction: isPii
            ? columnFlagUpdateOptions.addSensitive
            : columnFlagUpdateOptions.removeSensitive,
        },
      };

      updateFetch({ query: setDataSourceColumnMetadata, variables });
    },
    [updateFetch]
  );

  const updateAcesReference = useCallback(
    (columnData, isAcesReference) => {
      const variables = {
        metadata: {
          columnId: columnData.id,
          description: null,
          dataType: null,
          flagAction: isAcesReference
            ? columnFlagUpdateOptions.addAcesReference
            : columnFlagUpdateOptions.removeAcesReference,
        },
      };

      updateFetch({ query: setDataSourceColumnMetadata, variables });
    },
    [updateFetch]
  );

  const updateType = useCallback(
    (value, columnData) => {
      const variables = {
        metadata: {
          columnId: columnData.id,
          description: columnData.description,
          dataType: value.value,
          flagAction: columnFlagUpdateOptions.none,
        },
      };

      updateFetch({ query: setDataSourceColumnMetadata, variables });
    },
    [updateFetch]
  );

  useEffect(() => {
    if (updateData) {
      getUpdatedSource();
      toast.success("Column Updated", {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [updateData, getUpdatedSource]);

  const columnsData = [
    {
      Header: "Column Name",
      id: "name",
      accessor: (d) => d?.name,
    },
    {
      Header: "Data Type",
      id: "datatype",
      accessor: (d) => d.dataType,

      Cell: ({ row: { original } }) => {
        return (
          <div style={{ minWidth: "200px" }}>
            <StyledSelect
              className={`react-select-container`}
              classNamePrefix={`react-select`}
              name={`dataType`}
              id={`${original?.name}-dataType`}
              inputId={`${original?.name}-dataType-input`}
              instanceId={`${original?.name}-dataType-instance`}
              label={"Data Type"}
              menuPortalTarget={document.body}
              options={typeOptions}
              placeholder={`Select Type`}
              value={typeOptions.find(
                (type) => type.value === original.dataType
              )}
              menuPlacement="auto"
              onChange={(e) => updateType(e, original)}
            />
          </div>
        );
      },
    },
    {
      Header: "Nullable",
      id: "nullable",
      accessor: (d) => flagValue(d?.flags, COLUMN_FLAGS.NULLABLE),
      width: 120,
      Cell: ({ row: { original } }) => {
        /*
          1 = Nullable
          2 = Unique
          4 = PK
          16 = Matching
          32 = AsOfDate
          64 = TrackUnique/CodeBreak
          128 = Sensitive
        */
        const flags = original?.flags;
        const isPK = (flags & 4) > 0;
        const isAsOfDate = (flags & 32) > 0;
        const isMatching = (flags & 16) > 0;
        const nullable = (flags & 1) > 0;

        return (
          <>
            <FormControl>
              <input
                disabled={isMatching || isAsOfDate || updating || isPK}
                type="checkbox"
                name={`columns.${original.id}.nullable`}
                label="Can Be Null"
                checked={nullable}
                onChange={() => updateNull(original, !nullable)}
              />
            </FormControl>
          </>
        );
      },
    },
    {
      Header: "Primary Key",
      id: "primarykey",
      accessor: (d) => flagValue(d?.flags, COLUMN_FLAGS.PRIMARY_KEY),
      width: 120,
      Cell: ({ row: { original } }) => {
        const flags = original?.flags;
        /*
          1 = Nullable
          2 = Unique
          4 = PK
          16 = Matching
          32 = AsOfDate
          64 = TrackUnique/CodeBreak
          128 = Sensitive
        */
        const isPk = (flags & 4) > 0;
        const isAsOfDate = (flags & 32) > 0;
        return (
          <>
            <FormControl>
              <input
                disabled={updating || isAsOfDate}
                type="checkbox"
                name={`columns.${original.id}.primarykey`}
                label="Primary Key"
                checked={isPk}
                onChange={() => updatePrimaryKey(original, !isPk)}
              />
            </FormControl>
          </>
        );
      },
    },
    {
      Header: "Unique",
      id: "unique",
      accessor: (d) => flagValue(d?.flags, COLUMN_FLAGS.UNIQUE),
      width: 120,
      Cell: ({ row: { original } }) => {
        const flags = original?.flags;
        /*
          1 = Nullable
          2 = Unique
          4 = PK
          16 = Matching
          32 = AsOfDate
          64 = TrackUnique/CodeBreak
          128 = Sensitive
        */
        const isPK = (flags & 4) > 0;
        const isAsOfDate = (flags & 32) > 0;
        const isMatching = (flags & 16) > 0;
        const unique = (flags & 2) > 0;

        return (
          <>
            <FormControl>
              <input
                disabled={isAsOfDate || isMatching || updating || isPK}
                type="checkbox"
                name={`columns.${original.id}.unique`}
                label="Is Unique"
                checked={unique}
                onChange={() => updateUnique(original, !unique)}
              />
            </FormControl>
          </>
        );
      },
    },
    {
      Header: "Sensitive",
      id: "sensitive",
      accessor: (d) => flagValue(d?.flags, COLUMN_FLAGS.SENSITIVE),
      width: 120,
      Cell: ({ row: { original } }) => {
        const flags = original?.flags;
        /*
          1 = Nullable
          2 = Unique
          4 = PK
          16 = Matching
          32 = AsOfDate
          64 = TrackUnique/CodeBreak
          128 = Sensitive
        */
        const isSensitive = (flags & 128) > 0;

        return (
          <>
            <FormControl>
              <input
                disabled={updating}
                type="checkbox"
                name={`columns.${original.id}.sensitive`}
                label="Is Sensitive"
                checked={isSensitive}
                onChange={() => updateSensitive(original, !isSensitive)}
              />
            </FormControl>
          </>
        );
      },
    },
    ...(acesIntegrationEnabled
      ? [
          {
            // One column per data source; the server rejects a second one and names the column that has it
            Header: "ACES Reference",
            id: "acesReference",
            accessor: (d) => flagValue(d?.flags, COLUMN_FLAGS.ACES_REFERENCE),
            width: 150,
            Cell: ({ row: { original } }) => {
              const isAcesReference =
                ((original?.flags ?? 0) & COLUMN_FLAGS.ACES_REFERENCE) > 0;

              return (
                <>
                  <FormControl>
                    <input
                      disabled={updating}
                      type="checkbox"
                      name={`columns.${original.id}.acesReference`}
                      label="Is ACES Reference"
                      aria-label={`ACES Reference: ${original?.name}`}
                      checked={isAcesReference}
                      onChange={() =>
                        updateAcesReference(original, !isAcesReference)
                      }
                    />
                  </FormControl>
                </>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <ErrorMessages errors={[...(errors ?? []), ...(updateErrors ?? [])]} />
      <DataTable mode="clientSide" columnMenu defaultPageSize={50}
        dontReset={true}
        data={columns}
        columns={columnsData}
        loading={loading}
      />
      <ToastContainer />
    </>
  );
};

export default ColumnView;
